import AppLayout from '../../layouts/AppLayout';
import { LuImage } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { SITELINK } from '../../app-routes/Links';
import { useState } from 'react';
import { useCreateCommunity } from '../../api-services/community/use-create-community';
import ButtonComponent from '../../components/ButtonComponent';
import CommunityTopBar from './CommunityTopBar';

// ─── Image Compression Utility ───────────────────────────────────────────────
const compressImage = (file: File, maxWidth = 756, quality = 0.60): Promise<File> => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), {
            type: 'image/webp',
            lastModified: Date.now(),
          });
          resolve(compressed);
        },
        'image/webp',
        quality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
};

// main-component-ui
const CreateCommunity = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [compressing, setCompressing] = useState<boolean>(false);
  const [communityName, setCommunityName] = useState('');
  const [communityDescription, setCommunityDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  // const [tags, setTags] = useState<string[]>(['']);

  const navigate = useNavigate();
  const createNewCommunity = useCreateCommunity();

  const onSubmit = async (data: any) => {
    createNewCommunity.mutate(data);
  };

  if (createNewCommunity.isSuccess) {
    setLoading(false);
    navigate(`${SITELINK.COMMUNITIES}`);
  }

  // const handleAddInput = () => {
  //   setTags([...tags, '']);
  // };

  // const handleRemoveInput = (index: number) => {
  //   if (tags.length > 1) {
  //     const newTags = tags.filter((_, i) => i !== index);
  //     setTags(newTags);
  //   }
  // };

  // const handleInputChange = (index: number, value: string) => {
  //   const newTags = tags.map((input, i) => (i === index ? value : input));
  //   setTags(newTags);
  // };

  const handleUpload = async (value: FileList | null) => {
    if (value !== null && value.length > 0) {
      setCompressing(true);
      const compressed = await compressImage(value[0]);
      setFile(compressed);
      setCompressing(false);
    }
  };

  // const appendArrayToFormData = (formData: FormData, key: string, array: number[] | string[]) => {
  //   array.forEach((value) => formData.append(key, value.toString()));
  // };

  const addCommunity = () => {
    if (file !== null) {
      setLoading(true);
      const formData = new FormData();
      // appendArrayToFormData(formData, 'community_tags[]', tags);
      formData.append('community_banner', file);
      formData.append('community_name', communityName);
      formData.append('community_description', communityDescription);
      onSubmit(formData);
    }
  };

  return (
    <AppLayout title="Catapot - Create New Community">
      <CommunityTopBar />

      {/* Page title bar */}
      <div className="flex justify-between px-3 py-3 bg-white dark:bg-neutral-800 border-b dark:border-neutral-700">
        <div className="flex gap-4">
          <p className="mt-1 text-[20px] font-medium madimi-one dark:text-white">New Community</p>
        </div>
      </div>

      {/* Wrap everything in a flex column that fills the screen */}
      <div className="flex flex-col h-[calc(100vh-112px)] bg-white dark:bg-neutral-800">

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">

          {/* Hero / intro block */}
          <div className="text-center px-3 py-3 bg-white dark:bg-neutral-800 border-b dark:border-neutral-700">
            <div className="gap-4 py-6">
              <h3 className="mt-1 text-[22px] font-bold dark:text-white">Create Communities</h3>
              <p className="mt-1 text-[12px] font-medium text-[#687684] dark:text-white/80">
                Are you looking to build a community around a shared interest, goal, or passion? Create
                a Community
              </p>
            </div>
          </div>

          {/* Form area */}
          <div className="composer-area bg-white dark:bg-neutral-800">
            <div className="pb-4 pt-0 sm:pb-4">
              <div className="mt-0 sm:ml-0 sm:mt-0 sm:text-left">
                <div className="sm:mx-auto sm:w-full">
                  <form
                    className="space-y-3"
                    onSubmit={(e) => {
                      e.preventDefault();
                      addCommunity();
                    }}
                  >
                    <div className="flex-1 rounded-lg px-4 pb-4 pt-4">
                      <div className="w-full">

                        {/* Community Name */}
                        <div className="mt-2">
                          <input
                            name="firstName"
                            type="text"
                            value={communityName}
                            onChange={(e) => setCommunityName(e.target.value)}
                            required
                            className="peer block w-full rounded-md border border-gray-200 dark:border-neutral-600 py-[9px] pl-4 text-sm outline-2 placeholder:text-gray-400 dark:placeholder:text-neutral-500 font-semibold text-[12px] bg-white dark:bg-neutral-700 text-gray-900 dark:text-white"
                            placeholder="Name of Community"
                          />
                        </div>

                        {/* Community Description */}
                        <div className="mt-3">
                          <textarea
                            name="description"
                            value={communityDescription}
                            onChange={(e) => setCommunityDescription(e.target.value)}
                            required
                            className="peer block w-full rounded-md border border-gray-200 dark:border-neutral-600 py-[9px] pl-4 text-sm outline-2 placeholder:text-gray-400 dark:placeholder:text-neutral-500 font-semibold text-[12px] h-[85px] bg-white dark:bg-neutral-700 text-gray-900 dark:text-white"
                            placeholder="Description"
                          />
                        </div>

                        {/* Tags section — commented out */}
                        {/* <div className="w-full mt-3">
                          <div>
                            {tags.map((tag, index) => (
                              <div key={index} className="mt-1 relative">
                                <input
                                  type="text"
                                  value={tag}
                                  className="peer block w-full rounded-md border border-gray-200 dark:border-neutral-600 py-[9px] pl-4 text-sm outline-2 placeholder:text-gray-400 dark:placeholder:text-neutral-500 font-semibold text-[12px] bg-white dark:bg-neutral-700 text-gray-900 dark:text-white"
                                  onChange={(e) => handleInputChange(index, e.target.value)}
                                  placeholder="Community Tag Name"
                                />
                                <div className="flex justify-between mt-2">
                                  {tags.length !== 1 && (
                                    <button
                                      type="button"
                                      className="inline-flex text-xl justify-center rounded-md text-red-500 px-3 py-2 text-sm font-semibold w-auto absolute top-1 right-0"
                                      onClick={() => handleRemoveInput(index)}
                                    >
                                      <LuX />
                                    </button>
                                  )}
                                  {index === tags.length - 1 && (
                                    <button
                                      type="button"
                                      className="inline-flex justify-center rounded-md bg-green-500 px-3 py-1 text-sm font-semibold text-white shadow-sm w-auto"
                                      onClick={handleAddInput}
                                    >
                                      Add New Tag
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div> */}

                        {/* Banner upload — Composer-style */}
                        <div className="w-full mt-4">
                          <label
                            htmlFor="community-banner"
                            className="text-sm flex py-2 px-4 rounded-full shadow-sm gap-2 cursor-pointer border border-gray-200 dark:border-custom-600 bg-white dark:bg-custom-600 w-fit hover:bg-gray-50 dark:hover:bg-neutral-600 transition-colors"
                          >
                            <LuImage className="text-base text-custom-600 dark:text-white" />
                            <span className="text-custom-600 dark:text-white font-medium text-xs">
                              {compressing
                                ? 'Compressing…'
                                : file
                                ? file.name
                                : 'Add Banner Image'}
                            </span>
                            <input
                              id="community-banner"
                              type="file"
                              accept=".svg, .png, .jpeg, .jpg"
                              className="hidden"
                              onChange={(e) => handleUpload(e.currentTarget.files)}
                            />
                          </label>

                          {/* Compressing spinner */}
                          {compressing && (
                            <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500 dark:text-white/80">
                              <div className="w-4 h-4 border-2 border-custom-600 dark:border-custom-400 border-t-transparent rounded-full animate-spin" />
                              Optimising image…
                            </div>
                          )}

                          {/* Preview */}
                          {file && !compressing && (
                            <div className="mt-3">
                              <img
                                src={URL.createObjectURL(file)}
                                alt="Banner preview"
                                className="w-full h-[140px] object-cover rounded-lg border border-gray-200 dark:border-neutral-600"
                              />
                            </div>
                          )}
                        </div>

                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky bottom button */}
        <div className="px-4 pb-20 bg-white dark:bg-neutral-800 border-t dark:border-neutral-700 pt-4 sticky bottom-0">
          <ButtonComponent
            type="button"
            onClick={() => addCommunity()}
            disabled={loading || compressing}
            loading={loading}
            className="text-[13px] flex py-1 gap-1 w-full h-[50px] justify-center bg-custom-600 text-white rounded-lg px-5 border border-transparent font-medium disabled:opacity-60"
          >
            Create Community
          </ButtonComponent>
        </div>

      </div>
    </AppLayout>
  );
};

export default CreateCommunity;
